
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Info, ArrowRight, CreditCard, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useCustomerPortal } from '@/hooks/useSubscription';
import { useBusinessSubscriptionStatus } from '@/hooks/useBusinessSubscriptionStatus';
import { PATHS } from '@/routes/routePaths';

interface SubscriptionStatusProps {
  businessId: string;
  className?: string;
  showTitle?: boolean;
  isActive?: boolean;  // New prop for business active status
  discountType?: 'Paid' | 'Free' | null;  // New prop for business discount type
}

// Helper to get a friendly display of the status
const getStatusDisplay = (
  status: string | null, 
  isInGracePeriod: boolean,
  cancelAtPeriodEnd: boolean,
  hasActiveSubscription: boolean
): {
  label: string;
  color: string;
  description: string;
  badgeVariant: "default" | "outline" | "secondary";
} => {
  if (isInGracePeriod) {
    return {
      label: 'Canceled - Active',
      color: 'text-amber-600',
      description: 'Your subscription is canceled but remains active until the period ends',
      badgeVariant: "outline"
    };
  }

  if (cancelAtPeriodEnd && hasActiveSubscription) {
    return {
      label: 'Canceling',
      color: 'text-amber-600',
      description: 'Your subscription is active but will not renew',
      badgeVariant: "outline"
    };
  }

  if (!hasActiveSubscription) {
    return { 
      label: 'No Subscription',
      color: 'text-muted-foreground',
      description: 'Your business has no active subscription',
      badgeVariant: "outline"
    };
  }

  if (!status) {
    return { 
      label: 'No Subscription',
      color: 'text-muted-foreground',
      description: 'Your business has no active subscription',
      badgeVariant: "outline"
    };
  }

  switch (status) {
    case 'active':
      return {
        label: 'Active',
        color: 'text-green-600',
        description: 'Your subscription is active',
        badgeVariant: "default"
      };
    case 'past_due':
      return {
        label: 'Past Due',
        color: 'text-amber-600',
        description: 'Your subscription payment is past due',
        badgeVariant: "outline"
      };
    case 'trialing':
      return {
        label: 'Trial',
        color: 'text-blue-600',
        description: 'Your subscription is in trial period',
        badgeVariant: "secondary"
      };
    default:
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        color: 'text-gray-600',
        description: `Subscription status: ${status}`,
        badgeVariant: "secondary"
      };
  }
};

const SubscriptionStatus = ({ 
  businessId, 
  className = '',
  showTitle = true,
  isActive = true,  // Default to true for backward compatibility
  discountType = null
}: SubscriptionStatusProps) => {
  const navigate = useNavigate();
  const { data: subscriptionInfo, isLoading } = useBusinessSubscriptionStatus(businessId);
  const { redirectToCustomerPortal, loading: portalLoading } = useCustomerPortal();

  // Don't show subscription status for free businesses
  if (discountType === 'Free') {
    return null;
  }

  // If the business is not active, show a different message
  if (!isActive) {
    return (
      <Card className={`shadow-sm ${className}`}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <CreditCard className="h-5 w-5 text-primary" />
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center bg-amber-50 p-2 rounded border border-amber-200 text-xs">
            <Info className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" />
            <span className="text-amber-800">
              Your business is pending validation. Subscription options will be available once your business is approved.
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleManageSubscription = async () => {
    try {
      await redirectToCustomerPortal(businessId);
    } catch (error) {
      console.error("Error opening customer portal:", error);
    }
  };

  const handleViewPlans = () => {
    navigate(PATHS.subscription(businessId));
  };

  if (isLoading) {
    return (
      <Card className={`shadow-sm ${className}`}>
        <CardContent className="flex items-center justify-center space-y-4 py-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2">Loading subscription info...</span>
        </CardContent>
      </Card>
    );
  }

  const hasActiveSubscription = subscriptionInfo?.hasActiveSubscription || false;
  const planName = subscriptionInfo?.planName;
  const status = subscriptionInfo?.subscriptionStatus;
  const isInGracePeriod = subscriptionInfo?.isInGracePeriod || false;
  const cancelAtPeriodEnd = subscriptionInfo?.cancelAtPeriodEnd || false;
  const subscriptionEndDate = subscriptionInfo?.subscriptionEndDate;
  
  const statusDisplay = getStatusDisplay(status, isInGracePeriod, cancelAtPeriodEnd, hasActiveSubscription);

  const formattedEndDate = subscriptionEndDate 
    ? format(new Date(subscriptionEndDate), 'MMM dd, yyyy') 
    : null;

  return (
    <Card className={className}>
      {showTitle && (
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <CreditCard className="h-5 w-5 text-primary" />
            Subscription
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center justify-between">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1">
              <div className="flex items-center">
                <Badge 
                  variant={statusDisplay.badgeVariant} 
                  className={`
                    ${hasActiveSubscription && !isInGracePeriod ? "bg-green-50 hover:bg-green-100 text-green-700 border-green-200" : 
                      isInGracePeriod ? "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200" :
                      "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"}
                    mr-2
                  `}
                >
                  {statusDisplay.label}
                </Badge>
                <span className="font-medium">{planName}</span>
              </div>
              
              {subscriptionEndDate && hasActiveSubscription && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {isInGracePeriod ? 'Expires: ' : 'Renews: '}{formattedEndDate}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {hasActiveSubscription && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleViewPlans}
                  className="hidden sm:inline-flex text-primary hover:text-primary-dark"
                >
                  View Plans
                </Button>
              )}
              <Button 
                variant={hasActiveSubscription ? "outline" : "default"} 
                size="sm"
                onClick={hasActiveSubscription ? handleManageSubscription : handleViewPlans}
                disabled={portalLoading}
                className="whitespace-nowrap"
              >
                {portalLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    {hasActiveSubscription ? "Manage" : "Subscribe"}
                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {!hasActiveSubscription && !isInGracePeriod && (
          <div className="mt-2 flex items-center bg-amber-50 p-2 rounded border border-amber-200 text-xs">
            <Info className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" />
            <span className="text-amber-800">
              Your subscription is inactive. Please subscribe to access premium features.
            </span>
          </div>
        )}

        {isInGracePeriod && (
          <div className="mt-2 flex items-center bg-amber-50 p-2 rounded border border-amber-200 text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" />
            <span className="text-amber-800">
              Your subscription has been canceled and will expire on {formattedEndDate}. 
              You can reactivate your subscription from the Manage Subscription page.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatus;
